import React, { useEffect, useState } from 'react';

interface DeletionStep {
  step: string;
  progress: number;
  status: 'running' | 'success' | 'error';
}

interface DeletionProgressPopupProps {
  url: string;
  method?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeletionProgressPopup: React.FC<DeletionProgressPopupProps> = ({
  url,
  method = 'DELETE',
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [steps, setSteps] = useState<DeletionStep[]>([]);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset state on close
      setSteps([]);
      setCurrentProgress(0);
      setIsFinished(false);
      setHasError(false);
      return;
    }

    const startDeletion = async () => {
      try {
        const token =
          sessionStorage.getItem('access_token') ||
          localStorage.getItem('access_token') ||
          sessionStorage.getItem('token') ||
          localStorage.getItem('token') ||
          sessionStorage.getItem('auth_token') ||
          localStorage.getItem('auth_token');
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(url, {
          method,
          headers,
          credentials: 'include',
        });

        if (!response.ok || !response.body) {
          let errorDetail = `Failed with status ${response.status}`;
          try {
            const errText = await response.text();
            const errJson = JSON.parse(errText);
            errorDetail = errJson.message || errJson.detail || errJson.error || errorDetail;
          } catch (_) {}
          throw new Error(errorDetail);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            setIsFinished(true);
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n').filter(line => line.trim() !== '');

          for (const line of lines) {
            try {
              const data: DeletionStep = JSON.parse(line);
              setSteps(prev => [...prev, data]);
              setCurrentProgress(data.progress);
              
              if (data.status === 'error') {
                setHasError(true);
                setIsFinished(true);
                reader.cancel();
                return;
              }
              if (data.status === 'success') {
                setIsFinished(true);
              }
            } catch (e) {
              console.error('Error parsing stream JSON chunk', e, line);
            }
          }
        }
      } catch (error: any) {
        console.error('Deletion error:', error);
        setHasError(true);
        setIsFinished(true);
        setSteps(prev => [
          ...prev, 
          { step: error?.message || 'A network or server error occurred.', progress: 100, status: 'error' }
        ]);
      }
    };

    startDeletion();
  }, [isOpen, url, method]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-[#1C2127] rounded-lg p-6 w-[500px] text-white shadow-xl">
        <h2 className="text-xl font-bold mb-4">Deleting Records</h2>
        
        <div className="w-full bg-gray-700 rounded-full h-2.5 mb-4">
          <div 
            className={`h-2.5 rounded-full transition-all duration-300 ${hasError ? 'bg-red-500' : 'bg-[#0084CF]'}`}
            style={{ width: `${currentProgress}%` }}
          ></div>
        </div>

        <div className="bg-[#0D1117] rounded p-4 max-h-60 overflow-y-auto mb-4 font-mono text-sm">
          {steps.map((s, idx) => (
            <div key={idx} className="mb-2">
              {s.status === 'success' && <span className="text-green-500 mr-2">✔</span>}
              {s.status === 'running' && <span className="text-[#0084CF] mr-2">⟳</span>}
              {s.status === 'error' && <span className="text-red-500 mr-2">✖</span>}
              <span className={s.status === 'error' ? 'text-red-400' : 'text-gray-300'}>
                {s.step}
              </span>
            </div>
          ))}
          {!isFinished && steps.length === 0 && (
            <div className="text-gray-500 italic">Initializing deletion sequence...</div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          {isFinished && (
            <button
              onClick={() => {
                onClose();
                if (!hasError) onSuccess();
              }}
              className={`px-4 py-2 rounded text-white ${hasError ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {hasError ? 'Close' : 'Done'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
